import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useWA, useLoad, useDebounced, useSelection, PageHead, Card, Tabs, Search, Btn, Badge, StatusBadge, Drawer, Modal, Field, ImageInput, BulkBar, Check, Pager, Skeleton, Empty, ErrorBox, Avatar, num, money, ago, fdate, fdt, exportCsv } from "../ui";
import { siteLink } from "../api";

const TAKE = 40;

export function SendMessageModal({ to, onClose, onSent }) {
  const { run } = useWA();
  const [f, setF] = useState({ title: "", body: "", link: "", kind: "info" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const params = { ...f };
      if (to.all) params.to = "all";
      else params.userIds = to.ids;
      await run("noticeSend", params, "Message भेज दिया गया");
      onSent && onSent();
      onClose();
    } catch (e) {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={to.all ? "सभी users को message" : `Message भेजें — ${to.label}`} width={520}
      footer={<><Btn onClick={onClose}>रद्द</Btn><Btn variant="primary" icon="fa-solid fa-paper-plane" loading={busy} onClick={send} disabled={!f.title.trim() || !f.body.trim()}>भेजें</Btn></>}>
      <p className="text-xs wa-muted mb-4">यह message user को वेबसाइट पर 🔔 Notifications में दिखेगा।</p>
      <div className="space-y-3">
        <Field label="Title"><input autoFocus className="wa-input" value={f.title} maxLength={120} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="जैसे: आपका blog featured हुआ 🎉" /></Field>
        <Field label="Message"><textarea rows={5} className="wa-input" value={f.body} maxLength={2000} onChange={(e) => setF({ ...f, body: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Link (optional)" hint="जैसे /subscription"><input className="wa-input" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} /></Field>
          <Field label="Type">
            <select className="wa-input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="info">ℹ️ Info</option>
              <option value="success">✅ Good news</option>
              <option value="warning">⚠️ Warning</option>
              <option value="alert">🚨 Important</option>
            </select>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function GrantModal({ email, onClose, onDone }) {
  const { api, run } = useWA();
  const [plans, setPlans] = useState([]);
  const [f, setF] = useState({ email: email || "", plan: "", days: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api("settingsGet").then((r) => {
      setPlans(r.settings.plans);
      setF((x) => ({ ...x, plan: x.plan || (r.settings.plans[0] && r.settings.plans[0].k) }));
    }).catch(() => null);
  }, [api]);
  const go = async () => {
    setBusy(true);
    try {
      await run("payGrant", f, "Plan दे दिया गया");
      onDone && onDone();
      onClose();
    } catch (e) {
      setBusy(false);
    }
  };
  const p = plans.find((x) => x.k === f.plan);
  return (
    <Modal open onClose={onClose} title="हाथ से plan दें (free)" width={480}
      footer={<><Btn onClick={onClose}>रद्द</Btn><Btn variant="success" icon="fa-solid fa-gift" loading={busy} onClick={go}>Plan दें</Btn></>}>
      <div className="space-y-3">
        <Field label="User email"><input className="wa-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Plan">
          <select className="wa-input" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
            {plans.map((x) => <option key={x.k} value={x.k}>{x.name} — {x.days} दिन, {x.limit} videos</option>)}
          </select>
        </Field>
        <Field label="कितने दिन" hint={p ? `खाली छोड़ें तो ${p.days} दिन` : ""}><input type="number" min="1" className="wa-input" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} /></Field>
        <Field label="कारण (optional)"><input className="wa-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="जैसे: contest winner" /></Field>
      </div>
    </Modal>
  );
}

function UserDrawer({ id, onClose, onChanged }) {
  const { api, run, confirm } = useWA();
  const [u, setU] = useState(null);
  const [tab, setTab] = useState("activity");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(false);
  const [grant, setGrant] = useState(false);

  const load = () =>
    api("userDetail", { id }).then((r) => {
      setU(r);
      setForm({ name: r.name || "", username: r.username || "", bio: r.bio || "", website: r.website || "", image: r.image || "" });
    }).catch(() => onClose());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const act = async (action, params, okMsg, ask) => {
    if (ask && !(await confirm(ask))) return false;
    try {
      await run(action, params, okMsg);
      onChanged();
      return true;
    } catch (e) {
      return false;
    }
  };
  const saveProfile = async () => {
    setBusy(true);
    if (await act("userUpdate", { id, ...form }, "Profile save हो गया")) load();
    setBusy(false);
  };

  return (
    <Drawer open onClose={onClose} width={720}
      title={u ? <span className="flex items-center gap-2">{u.name || u.email}{u.blocked && <Badge tone="red">Blocked</Badge>}{u.activePlan && <Badge tone="green" icon="fa-solid fa-crown">{u.activePlan.plan}</Badge>}</span> : "User"}
      subtitle={u && <>{u.email} · जुड़े {fdate(u.at)}</>}
      footer={u && <>
        <Btn className="!text-rose-600 mr-auto" icon="fa-regular fa-trash-can" onClick={async () => { if (await act("userDelete", { id }, "User delete हो गया", { title: `${u.name || u.email} को हमेशा के लिए delete करें?`, text: "उसके सारे blogs, posters, comments, likes और payments भी हट जाएँगे। यह वापस नहीं होगा।", danger: true })) onClose(); }}>Delete</Btn>
        <Btn icon="fa-solid fa-paper-plane" onClick={() => setMsg(true)}>Message</Btn>
        {u.blocked
          ? <Btn variant="success" icon="fa-solid fa-unlock" onClick={async () => { if (await act("userBlock", { id, blocked: false }, "User unblock हो गया")) load(); }}>Unblock</Btn>
          : <Btn variant="warn" icon="fa-solid fa-ban" onClick={async () => { if (await act("userBlock", { id, blocked: true }, "User block हो गया", { title: "इस user को block करें?", text: "वह वेबसाइट पर login नहीं कर पाएगा। बाद में unblock कर सकते हैं।", ok: "Block करें", danger: true })) load(); }}>Block</Btn>}
      </>}>
      {!u ? <Skeleton rows={8} /> : (
        <>
          <div className="flex items-center gap-4 mb-5">
            <Avatar name={u.name || u.email} image={u.image} size={64} />
            <div className="grid grid-cols-4 gap-2 flex-1 text-center">
              {[["Blogs", u.blogs.length], ["Views", u.totalViews], ["Posters", u.posters.length], ["Likes दिए", u.likes]].map(([l, v]) => (
                <div key={l} className="wa-card py-2.5 px-1"><p className="text-lg font-extrabold tabular-nums">{num(v)}</p><p className="text-[11px] wa-muted">{l}</p></div>
              ))}
            </div>
          </div>
          <Tabs tabs={[["activity", "Activity"], ["profile", "Profile edit"], ["payments", "Payments", u.payments.length], ["messages", "Messages", u.notices.length]]} value={tab} onChange={setTab} />
          <div className="mt-4">
            {tab === "activity" && (
              <div className="space-y-4">
                <Card title={`Blogs (${u.blogs.length})`} pad={false}>
                  {!u.blogs.length ? <p className="text-sm wa-muted px-5 pb-4">कोई blog नहीं</p> : (
                    <ul>{u.blogs.map((b) => (
                      <li key={b.id} className="flex items-center gap-3 px-5 py-2.5 border-t wa-line">
                        <Link to={`/website/blogs/${b.id}`} className="flex-1 min-w-0 text-sm font-semibold truncate hover:underline">{b.title}</Link>
                        <span className="text-[12px] wa-muted tabular-nums">{num(b.views)} views</span>
                        <StatusBadge s={b.status} />
                      </li>))}</ul>
                  )}
                </Card>
                <Card title={`Posters (${u.posters.length})`} pad={false}>
                  {!u.posters.length ? <p className="text-sm wa-muted px-5 pb-4">कोई poster नहीं</p> : (
                    <ul>{u.posters.map((p) => (
                      <li key={p.id} className="flex items-center gap-3 px-5 py-2.5 border-t wa-line">
                        <Link to={`/website/posters?open=${p.id}`} className="flex-1 min-w-0 text-sm font-semibold truncate hover:underline">{p.title}</Link>
                        <span className="text-[12px] wa-muted tabular-nums">{num(p.views)} views</span>
                      </li>))}</ul>
                  )}
                </Card>
                <Card title="हाल के comments" pad={false}>
                  {!u.comments.length ? <p className="text-sm wa-muted px-5 pb-4">कोई comment नहीं</p> : (
                    <ul>{u.comments.map((c) => (
                      <li key={c.id} className="px-5 py-2.5 border-t wa-line">
                        <p className="text-sm">{c.text}</p>
                        <p className="text-[11.5px] wa-muted mt-0.5">{c.post} · {ago(c.at)}</p>
                      </li>))}</ul>
                  )}
                </Card>
              </div>
            )}
            {tab === "profile" && form && (
              <Card>
                <div className="space-y-3">
                  <ImageInput label="Photo" square value={form.image} folder="users" onChange={(v) => setForm({ ...form, image: v })} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="नाम"><input className="wa-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                    <Field label="Username"><input className="wa-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></Field>
                  </div>
                  <Field label="Bio"><textarea rows={3} className="wa-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></Field>
                  <Field label="Website"><input className="wa-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
                  <p className="text-[11.5px] wa-muted">Email नहीं बदला जा सकता — यह YCNP app account से जुड़ा है।</p>
                  <div className="flex justify-end"><Btn variant="primary" loading={busy} onClick={saveProfile}>Profile save करें</Btn></div>
                </div>
              </Card>
            )}
            {tab === "payments" && (
              <Card title="Payments" actions={<Btn size="sm" variant="soft" icon="fa-solid fa-gift" onClick={() => setGrant(true)}>Plan दें</Btn>} pad={false}>
                {!u.payments.length ? <p className="text-sm wa-muted px-5 pb-4">कोई payment नहीं</p> : (
                  <ul>{u.payments.map((p) => (
                    <li key={p.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5 border-t wa-line text-sm">
                      <b>{p.plan}</b><span className="tabular-nums">{money(p.amount)}</span><span className="text-[12px] wa-muted">UTR {p.utr}</span>
                      <span className="text-[12px] wa-muted">{fdate(p.createdAt)}{p.expiresAt ? ` → ${fdate(p.expiresAt)}` : ""}</span>
                      <span className="ml-auto"><StatusBadge s={p.status} /></span>
                    </li>))}</ul>
                )}
                <div className="px-5 py-3 border-t wa-line"><Link to={`/website/payments?status=ALL&q=${encodeURIComponent(u.email || "")}`} className="text-xs font-semibold" style={{ color: "var(--wa-accent)" }}>Payments page पर देखें →</Link></div>
              </Card>
            )}
            {tab === "messages" && (
              <Card title="भेजे गए messages" actions={<Btn size="sm" variant="soft" icon="fa-solid fa-paper-plane" onClick={() => setMsg(true)}>नया message</Btn>} pad={false}>
                {!u.notices.length ? <p className="text-sm wa-muted px-5 pb-4">अभी तक कोई message नहीं भेजा</p> : (
                  <ul>{u.notices.map((n) => (
                    <li key={n.id} className="px-5 py-3 border-t wa-line">
                      <p className="text-sm font-semibold">{n.title}</p>
                      <p className="text-sm wa-muted whitespace-pre-wrap">{n.body}</p>
                      <p className="text-[11px] wa-muted mt-1">{fdt(n.createdAt)}</p>
                    </li>))}</ul>
                )}
              </Card>
            )}
          </div>
          {u.email && <a href={`mailto:${u.email}`} className="inline-block mt-4 text-xs underline wa-muted">Email से संपर्क करें</a>}
        </>
      )}
      {msg && u && <SendMessageModal to={{ ids: [id], label: u.name || u.email }} onClose={() => setMsg(false)} onSent={load} />}
      {grant && u && <GrantModal email={u.email} onClose={() => setGrant(false)} onDone={load} />}
    </Drawer>
  );
}

export default function Users() {
  const { run, confirm } = useWA();
  const loc = useLocation();
  const nav = useNavigate();
  const qs = new URLSearchParams(loc.search);
  const open = qs.get("open");
  const [filter, setFilter] = useState(qs.get("filter") || "");
  const [sort, setSort] = useState("new");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [msgTo, setMsgTo] = useState(null);
  const dq = useDebounced(q);
  useEffect(() => setSkip(0), [dq, filter, sort]);
  const { data, loading, error, reload } = useLoad("users", { q: dq, filter, sort, take: TAKE, skip }, [dq, filter, sort, skip]);
  const items = (data && data.items) || [];
  const S = useSelection(items);

  const bulk = async (action, params, msg, ask) => {
    if (ask && !(await confirm(ask))) return;
    try {
      await run(action, params, msg);
      reload();
    } catch (e) {
      /* toast */
    }
  };

  return (
    <div>
      <PageHead icon="fa-solid fa-users" title="Users" subtitle={data ? `कुल ${num(data.total)} users` : "वेबसाइट के users"}
        actions={
          <>
            <Btn icon="fa-solid fa-download" disabled={!items.length} onClick={() => exportCsv(items.map((u) => ({ name: u.name, email: u.email, username: u.username, joined: u.at, blocked: u.blocked, blogs: u.blogs, posters: u.posters, comments: u.comments, likes: u.likes })), "ycnp-users")}>CSV</Btn>
            <Btn icon="fa-solid fa-gift" onClick={() => setMsgTo({ grant: true })}>Plan दें</Btn>
            <Btn variant="primary" icon="fa-solid fa-bullhorn" onClick={() => setMsgTo({ all: true })}>सबको message</Btn>
          </>
        } />
      <div className="flex flex-col gap-3 mb-4">
        <Tabs tabs={[["", "सभी"], ["active", "Active"], ["blocked", "Blocked"], ["writers", "Writers"], ["paid", "Paid plan"]]} value={filter} onChange={setFilter} />
        <div className="flex flex-wrap gap-2">
          <Search value={q} onChange={setQ} placeholder="नाम, email या username…" className="flex-1 min-w-[220px]" />
          <select className="wa-input !w-auto" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
            <option value="new">नए पहले</option>
            <option value="old">पुराने पहले</option>
            <option value="name">नाम A–Z</option>
          </select>
        </div>
      </div>
      <BulkBar count={S.sel.length} onClear={S.clear}>
        <Btn size="sm" variant="primary" icon="fa-solid fa-paper-plane" onClick={() => setMsgTo({ ids: S.sel, label: `${S.sel.length} users` })}>Message</Btn>
        <Btn size="sm" variant="warn" icon="fa-solid fa-ban" onClick={() => bulk("userBlock", { ids: S.sel, blocked: true }, "Users block हो गए", { title: `${S.sel.length} users block करें?`, danger: true, ok: "Block" })}>Block</Btn>
        <Btn size="sm" variant="success" icon="fa-solid fa-unlock" onClick={() => bulk("userBlock", { ids: S.sel, blocked: false }, "Users unblock हो गए")}>Unblock</Btn>
        <Btn size="sm" variant="danger" icon="fa-regular fa-trash-can" onClick={() => bulk("userDelete", { ids: S.sel }, "Users delete हो गए", { title: `${S.sel.length} users हमेशा के लिए delete करें?`, text: "उनका सारा content भी हट जाएगा।", danger: true })}>Delete</Btn>
      </BulkBar>
      <ErrorBox msg={error} retry={reload} />
      <Card pad={false}>
        {loading && !data ? <div className="p-4"><Skeleton rows={8} h={52} /></div> : !items.length ? <Empty icon="fa-solid fa-user" title="कोई user नहीं मिला" /> : (
          <div className="overflow-x-auto">
            <table className="wa-tbl">
              <thead>
                <tr>
                  <th style={{ width: 36 }}><Check checked={S.all} onChange={S.toggleAll} label="Select all" /></th>
                  <th>User</th>
                  <th className="text-right hidden sm:table-cell">Blogs</th>
                  <th className="text-right hidden md:table-cell">Posters</th>
                  <th className="text-right hidden lg:table-cell">Comments</th>
                  <th className="hidden md:table-cell">जुड़े</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id} className={S.has(u.id) ? "wa-sel" : ""}>
                    <td><Check checked={S.has(u.id)} onChange={() => S.toggle(u.id)} /></td>
                    <td className="min-w-[240px]">
                      <button type="button" onClick={() => nav(`/website/users?open=${u.id}`)} className="flex items-center gap-3 text-left">
                        <Avatar name={u.name || u.email} image={u.image} />
                        <span className="min-w-0">
                          <span className="block font-semibold truncate hover:underline">{u.name || "—"} {u.blocked && <Badge tone="red" className="ml-1">Blocked</Badge>}</span>
                          <span className="block text-[12px] wa-muted truncate">{u.email}{u.username ? ` · @${u.username}` : ""}</span>
                        </span>
                      </button>
                    </td>
                    <td className="text-right tabular-nums hidden sm:table-cell">{num(u.blogs)}</td>
                    <td className="text-right tabular-nums hidden md:table-cell">{num(u.posters)}</td>
                    <td className="text-right tabular-nums hidden lg:table-cell">{num(u.comments)}</td>
                    <td className="hidden md:table-cell text-[12.5px] wa-muted whitespace-nowrap">{fdate(u.at)}</td>
                    <td className="text-right whitespace-nowrap">
                      <Btn size="sm" icon="fa-solid fa-paper-plane" onClick={() => setMsgTo({ ids: [u.id], label: u.name || u.email })} aria-label="Message" title="Message" />
                      <Btn size="sm" className="ml-1" icon={u.blocked ? "fa-solid fa-unlock" : "fa-solid fa-ban"} title={u.blocked ? "Unblock" : "Block"} aria-label={u.blocked ? "Unblock" : "Block"}
                        onClick={() => bulk("userBlock", { id: u.id, blocked: !u.blocked }, u.blocked ? "Unblock हो गया" : "Block हो गया", u.blocked ? null : { title: `${u.name || u.email} को block करें?`, danger: true, ok: "Block" })} />
                      <Btn size="sm" className="ml-1" icon="fa-solid fa-chevron-right" onClick={() => nav(`/website/users?open=${u.id}`)} aria-label="Open" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={data && data.total} take={TAKE} skip={skip} onChange={setSkip} />
      </Card>
      {open && <UserDrawer id={open} onClose={() => nav("/website/users")} onChanged={reload} />}
      {msgTo && !msgTo.grant && <SendMessageModal to={msgTo} onClose={() => setMsgTo(null)} />}
      {msgTo && msgTo.grant && <GrantModal onClose={() => setMsgTo(null)} />}
      <p className="text-xs wa-muted mt-4">Block किया हुआ user वेबसाइट पर login नहीं कर पाता। <a className="underline" href={siteLink("/")} target="_blank" rel="noreferrer">Website खोलें</a></p>
    </div>
  );
}

export { GrantModal };
